import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Box, Card, CardContent, Drawer, Fab, IconButton, Typography, useTheme } from '@mui/material';
import { MessageOutlined, CloseOutlined } from '@ant-design/icons';
import SimpleBar from 'components/third-party/SimpleBar';
import PremiumChatInput from 'components/chat/PremiumChatInput';
import { setChatContext, clearChatContexts, removeChatContext, setChatOpen } from 'store/reducers/chatContext';

const ChatMessage = ({ message, theme }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
      mb: 2
    }}
  >
    <Card
      sx={{
        maxWidth: '85%',
        background: message.type === 'user' 
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
          : theme.palette.grey[100],
        color: message.type === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
        borderRadius: message.type === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
        boxShadow: `0 2px 10px ${theme.palette.action.hover}`,
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="body1">{message.content}</Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.7, 
            display: 'block', 
            mt: 1,
            fontSize: '0.75rem'
          }}
        >
          {new Date(message.timestamp).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const ContextMessage = ({ context, theme, onRemove }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'flex-start',
      mb: 2
    }}
  >
    <Card
      sx={{
        maxWidth: '85%',
        background: `linear-gradient(135deg, ${theme.palette.info.light}, ${theme.palette.info.main})`,
        color: theme.palette.info.contrastText,
        borderRadius: '20px 20px 20px 4px',
        boxShadow: `0 2px 10px ${theme.palette.action.hover}`,
        border: `1px solid ${theme.palette.info.main}`,
        position: 'relative',
      }}
    >
      <IconButton
        onClick={() => onRemove(context.id)}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          color: theme.palette.info.contrastText,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          width: 20,
          height: 20,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <CloseOutlined style={{ fontSize: '12px' }} />
      </IconButton>
      <CardContent sx={{ p: 2, pr: 4, '&:last-child': { pb: 2 } }}>
        <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 'bold' }}>
          {context.selectionType === 'text' ? '📝 Text Selected' : '📋 Card Selected'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, maxHeight: '100px', overflow: 'auto' }}>
          {context.content}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.7, 
            display: 'block', 
            mt: 1,
            fontSize: '0.75rem'
          }}
        >
          {new Date(context.timestamp).toLocaleTimeString()}
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const ChatSidebar = ({ onToggle }) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(false);
  const chatContext = useSelector((state) => state.chatContext.context);
  const chatContexts = useSelector((state) => state.chatContext.contexts);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldShow = params.get('showChat') === 'true';
    setIsVisible(shouldShow);
    if (!shouldShow) {
      setIsOpen(false);
      onToggle(false);
    }
  }, [location.search, onToggle]);
  
  useEffect(() => {
    onToggle(isOpen);
    dispatch(setChatOpen(isOpen));
  }, [isOpen, onToggle, dispatch]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleRemoveContext = (contextId) => {
    dispatch(removeChatContext(contextId));
  };

  const handleSendMessage = async (message) => {
    // Combine all contexts into the message
    let userMessage = message;
    
    if (chatContexts.length > 0) {
      const contextParts = chatContexts.map((ctx, idx) => `Context ${idx + 1}: "${ctx.content}"`).join('\n\n');
      userMessage = `${contextParts}\n\nMessage: "${message}"`;
    } else if (chatContext) {
      // Fallback to old context for backward compatibility
      userMessage = `Context: "${chatContext}"\n\nMessage: "${message}"`;
    }
    
    const newMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    setShowTyping(true);
    
    setTimeout(() => {
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        content: `This is a demo response to "${message}".`,
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setShowTyping(false);
    }, 2000);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <Fab
          color="primary"
          aria-label="open chat"
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            zIndex: theme.zIndex.drawer + 1
          }}
        >
          <MessageOutlined />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleToggle}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            height: '100%',
            boxShadow: theme.customShadows.z1,
            borderLeft: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <Typography variant="h5">Chat</Typography>
            <IconButton onClick={handleToggle}>
              <CloseOutlined />
            </IconButton>
          </Box>
          {(chatContexts.length > 0 || chatContext) && (
            <Alert
              severity="info"
              onClose={() => {
                dispatch(setChatContext(null));
                dispatch(clearChatContexts());
              }}
              sx={{ m: 2, mt: 0 }}
            >
              <Typography variant="caption" display="block" gutterBottom>
                <b>{chatContexts.length > 0 ? `${chatContexts.length} Contexts Selected` : 'Context Selected'}:</b>
              </Typography>
              <Typography variant="caption">
                Click X to clear all contexts
              </Typography>
            </Alert>
          )}
          <SimpleBar sx={{ flexGrow: 1, p: 2 }}>
            {/* Display context messages */}
            {chatContexts.map((context) => (
              <ContextMessage key={context.id} context={context} theme={theme} onRemove={handleRemoveContext} />
            ))}
            {/* Display regular messages */}
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} theme={theme} />
            ))}
          </SimpleBar>
          <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
            <PremiumChatInput
              onSendMessage={handleSendMessage}
              placeholder="Ask anything..."
              showTyping={showTyping}
              autoFocus
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default ChatSidebar; 